import { Calendar } from "lucide-react"

interface CalendarEvent {
  id: string
  title: string
  description: string | null
  startTime: string
  endTime: string | null
  allDay: boolean
  type: string
  color: string
  client: {
    id: string
    name: string
    imageUrl: string | null
  } | null
  task: {
    id: string
    title: string
    status: string
    priority: string
  } | null
  createdBy: {
    username: string
    avatarUrl: string | null
  }
}

interface UpcomingEventsProps {
  events: CalendarEvent[]
}

export default function UpcomingEvents({ events }: UpcomingEventsProps) {
  // Get the next 7 days starting from today
  const getNext7Days = () => {
    const days = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      days.push(date)
    }

    return days
  }

  const getDayLabel = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const checkDate = new Date(date)
    checkDate.setHours(0, 0, 0, 0)

    const weekday = date.toLocaleDateString('pl-PL', { weekday: 'short' })

    if (checkDate.getTime() === today.getTime()) {
      return `Dzisiaj (${weekday})`
    } else if (checkDate.getTime() === tomorrow.getTime()) {
      return `Jutro (${weekday})`
    } else {
      return weekday
    }
  }

  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime)
      return eventDate.toDateString() === date.toDateString()
    })
  }

  const days = getNext7Days()
  const hasAnyEvents = events.length > 0

  if (!hasAnyEvents) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-50 mb-2">Brak nadchodzących wydarzeń</h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">Wydarzenia pojawią się tutaj, gdy zostaną dodane do kalendarza</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
      {days.map((date, index) => {
        const dayEvents = getEventsForDay(date)
        const isToday = date.toDateString() === new Date().toDateString()

        return (
          <div key={index} className="border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-zinc-700 last:border-b-0 sm:last:border-r-0">
            {/* Day Header */}
            <div className={`p-3 border-b border-gray-200 dark:border-zinc-700 text-center ${isToday ? 'bg-purple-50 dark:bg-purple-950' : 'bg-gray-50 dark:bg-zinc-800'}`}>
              <div className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                {getDayLabel(date)}
              </div>
              <div className={`text-lg font-bold ${isToday ? 'text-purple-600 dark:text-purple-400' : 'text-gray-900 dark:text-zinc-50'}`}>
                {date.getDate()}
              </div>
            </div>

            {/* Events for this day */}
            <div className="p-2 min-h-[200px] sm:min-h-[200px] space-y-1">
              {dayEvents.map((event) => (
                <div
                  key={event.id}
                  className="text-xs p-2 rounded cursor-pointer hover:opacity-75 transition-opacity"
                  style={{ backgroundColor: event.color + '20', color: event.color }}
                  onClick={() => window.location.href = '/calendar'}
                >
                  <div className="font-medium truncate">{event.title}</div>
                  {!event.allDay && (
                    <div className="text-xs opacity-75 mt-0.5">
                      {new Date(event.startTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      {event.endTime && ` - ${new Date(event.endTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}